import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import StarRoundedIcon from '@material-ui/icons/StarRounded';
import AssignmentTurnedInOutlinedIcon from '@material-ui/icons/AssignmentTurnedInOutlined';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import { ErrorLable, ServiceTypeLabel } from './MultiCheckbox';
import { ServiceTypeItemType, ServiceTypeItems, ServiceTypes } from '../../../../types';
import { RootState } from '../../../../../../redux/root';

type Props = {
  setValue: Function;
  errors: any;
  trigger: Function;
  register: Function;
};

const ICONS: Record<ServiceTypes, React.ComponentType<{ style?: React.CSSProperties }>> = {
  Permitting: AssignmentTurnedInOutlinedIcon,
  'Private Provider': VerifiedUserOutlinedIcon,
  Reviews: FindInPageOutlinedIcon,
  Inspection: BuildOutlinedIcon,
  Consulting: EmojiObjectsOutlinedIcon,
  Other: HelpOutlineOutlinedIcon,
};

const POPULAR: ServiceTypes[] = ['Private Provider'];

const ServiceType: FC<Props> = ({ setValue, errors, trigger, register }) => {
  const [itemsArr, setItemsArr] = useState<ServiceTypeItemType[]>(ServiceTypeItems);
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const showDrawerContent = useSelector((s: RootState) => s.general.showDrawerContent);
  const name = 'serviceType';

  useEffect(() => {
    if (!showDrawerContent) {
      const unchecked = itemsArr.map(i => ({ ...i, checked: false }));
      setItemsArr(unchecked);
    }
  }, [showDrawerContent]);

  useEffect(() => {
    if (initLoad) {
      setInitLoad(false);
      return;
    }
    register(name);
    setValue(name, itemsArr);
    const performTrigger = async () => {
      await trigger(name);
    };
    performTrigger();
  }, [itemsArr]);

  const toggle = (title: ServiceTypes) => {
    const target = itemsArr.find(i => i.title === title);
    if (!target) return;
    const nextChecked = !target.checked;
    const next = itemsArr.map(item => {
      if (item.title === title) {
        return { ...item, checked: nextChecked };
      }
      if (item.parent === title && !nextChecked) {
        return { ...item, checked: false };
      }
      return item;
    });
    setItemsArr(next);
  };

  const isChecked = (title: ServiceTypes) =>
    itemsArr.find(i => i.title === title)?.checked ?? false;

  const parents = itemsArr.filter(i => !i.parent);
  const childrenOf = (title: ServiceTypes) => itemsArr.filter(i => i.parent === title);
  const selected = itemsArr.filter(i => i.checked);
  const hasError = errors[name] !== undefined;

  return (
    <Wrapper>
      <Header>
        <ServiceTypeLabel error={hasError}>
          What services do you need?
          <Asterisk>*</Asterisk>
        </ServiceTypeLabel>
        <CountText>
          {selected.length === 0 ? 'Pick one or more' : `${selected.length} selected`}
        </CountText>
      </Header>
      {hasError && <ErrorLable>{errors[name].message}</ErrorLable>}

      <CardsList>
        {parents.map(parent => {
          const Icon = ICONS[parent.title];
          const checked = isChecked(parent.title);
          const isPopular = POPULAR.includes(parent.title);
          const kids = childrenOf(parent.title);
          return (
            <ServiceCardWrap key={parent.title}>
              <ServiceCard
                type="button"
                onClick={() => toggle(parent.title)}
                $checked={checked}
                aria-pressed={checked}
              >
                <CardIcon $checked={checked}>
                  <Icon style={{ fontSize: 24 }} />
                </CardIcon>
                <CardBody>
                  <CardTitleRow>
                    <CardTitle>{parent.title}</CardTitle>
                    {isPopular && (
                      <PopularBadge>
                        <StarRoundedIcon style={{ fontSize: 12 }} />
                        Most popular
                      </PopularBadge>
                    )}
                  </CardTitleRow>
                  {parent.description && <CardDesc>{parent.description}</CardDesc>}
                </CardBody>
                <CheckIndicator $checked={checked} aria-hidden>
                  {checked && <CheckRoundedIcon style={{ fontSize: 16 }} />}
                </CheckIndicator>
              </ServiceCard>

              {kids.length > 0 && checked && (
                <ChildPanel>
                  <ChildEyebrow>Add to {parent.title}</ChildEyebrow>
                  <ChildList>
                    {kids.map(kid => {
                      const KidIcon = ICONS[kid.title];
                      const kidChecked = isChecked(kid.title);
                      return (
                        <ChildCard
                          key={kid.title}
                          type="button"
                          onClick={() => toggle(kid.title)}
                          $checked={kidChecked}
                          aria-pressed={kidChecked}
                        >
                          <ChildIcon $checked={kidChecked}>
                            <KidIcon style={{ fontSize: 18 }} />
                          </ChildIcon>
                          <ChildBody>
                            <ChildTitle>{kid.title}</ChildTitle>
                            {kid.description && <ChildDesc>{kid.description}</ChildDesc>}
                          </ChildBody>
                          <CheckIndicator $checked={kidChecked} aria-hidden>
                            {kidChecked && <CheckRoundedIcon style={{ fontSize: 14 }} />}
                          </CheckIndicator>
                        </ChildCard>
                      );
                    })}
                  </ChildList>
                </ChildPanel>
              )}
            </ServiceCardWrap>
          );
        })}
      </CardsList>

      <SummaryBar $empty={selected.length === 0}>
        <SummaryLabel>Selected</SummaryLabel>
        {selected.length === 0 ? (
          <SummaryEmpty>Nothing selected yet</SummaryEmpty>
        ) : (
          <ChipList>
            {selected.map(s => (
              <Chip key={s.title}>{s.title}</Chip>
            ))}
          </ChipList>
        )}
      </SummaryBar>
    </Wrapper>
  );
};

export default ServiceType;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Asterisk = styled.span`
  color: var(--danger-500, #dc2626);
  margin-left: 2px;
`;

const CountText = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
`;

const CardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ServiceCardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ServiceCard = styled.button<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 14px;
  width: 100%;
  text-align: left;
  padding: 16px 18px;
  background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-0)')};
  border: 1.5px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--border)')};
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? 'var(--primary-700, #0b5a87)' : 'var(--neutral-400)')};
    background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 540px) {
    padding: 14px;
    gap: 12px;
  }
`;

const CardIcon = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--primary-50)')};
  color: ${({ $checked }) => ($checked ? 'var(--neutral-0)' : 'var(--primary-600)')};
  transition: background-color 0.15s ease, color 0.15s ease;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CardTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const PopularBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 20px;
  padding: 0 8px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 999px;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;

  svg {
    color: #d97706;
  }
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.45;
`;

const CheckIndicator = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-300)')};
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-0)')};
  color: var(--neutral-0);
  margin-top: 2px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`;

const ChildPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 24px;
  padding: 14px 16px;
  background: var(--neutral-50);
  border: 1px dashed var(--primary-100, #cfe6f3);
  border-radius: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -14px;
    top: 24px;
    width: 14px;
    height: 1px;
    border-top: 1px dashed var(--primary-100, #cfe6f3);
  }

  @media only screen and (max-width: 540px) {
    margin-left: 0;
    &::before {
      display: none;
    }
  }
`;

const ChildEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--primary-700, #0b5a87);
`;

const ChildList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media only screen and (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ChildCard = styled.button<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 10px;
  text-align: left;
  padding: 12px;
  background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-0)')};
  border: 1.5px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--border)')};
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? 'var(--primary-700, #0b5a87)' : 'var(--neutral-400)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ChildIcon = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--primary-50)')};
  color: ${({ $checked }) => ($checked ? 'var(--neutral-0)' : 'var(--primary-600)')};
`;

const ChildBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ChildTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--mainText);
`;

const ChildDesc = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--neutral-600);
  line-height: 1.4;
`;

const SummaryBar = styled.div<{ $empty: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ $empty }) => ($empty ? 'var(--neutral-50)' : 'var(--primary-50)')};
  border: 1px dashed ${({ $empty }) => ($empty ? 'var(--border)' : 'var(--primary-100, #cfe6f3)')};
  border-radius: 10px;
  flex-wrap: wrap;
`;

const SummaryLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  flex-shrink: 0;
`;

const SummaryEmpty = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-400);
  font-style: italic;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  background: var(--neutral-0);
  color: var(--primary-700, #0b5a87);
  border: 1px solid var(--primary-100, #cfe6f3);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
`;
